import { useEffect, useState } from 'react';
import type { PanelId } from '@cinegrid/core';
import { useEditorStore } from './state/editorStore';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { loadProjectSnapshot } from './media/projectStore';
import { projectWeight, tabSync } from './sync/tabSync';
import { ColorSuiteLayout } from './ui/layout/ColorSuiteLayout';
import { Toolbar } from './ui/layout/Toolbar';
import { InspectorPanel } from './ui/layout/InspectorPanel';
import { ResizeHandle } from './ui/layout/ResizeHandle';
import { MediaBinPanel } from './ui/panels/MediaBinPanel';
import { EffectsPanel } from './ui/panels/EffectsPanel';
import { ViewerPanel } from './ui/panels/ViewerPanel';
import { ScopesPanel } from './ui/panels/ScopesPanel';
import { TimelinePanel } from './ui/panels/TimelinePanel';
import { TimelineToolsRail } from './ui/layout/TimelineToolsRail';
import { WindowManagerPanel } from './ui/panels/WindowManagerPanel';
import { ColorPanel } from './ui/panels/ColorPanel';
import { KeyframesPanel } from './ui/panels/KeyframesPanel';
import { ExportPanel } from './ui/panels/ExportPanel';

function LeftBin() {
  const [tab, setTab] = useState<'media' | 'effects'>('media');
  const hasMedia = useEditorStore((s) => s.hasPanel('media'));
  const hasEffects = useEditorStore((s) => s.hasPanel('effects'));
  const leftWidth = useEditorStore((s) => s.panelSizes.leftWidth);
  const setPanelSize = useEditorStore((s) => s.setPanelSize);

  if (!hasMedia && !hasEffects) return null;
  return (
    <>
      <div
        className="flex h-full min-h-0 shrink-0 flex-col"
        style={{ width: leftWidth }}
      >
        {(hasMedia && hasEffects) && (
        <div className="flex shrink-0 gap-1 border-b border-hairline px-2 py-1.5">
          {hasMedia && (
            <button
              type="button"
              className={`flex-1 rounded-lg py-1.5 text-xs font-medium ${tab === 'media' ? 'bg-raised text-text-high' : 'text-text-low hover:text-text-high'}`}
              onClick={() => setTab('media')}
            >
              Media
            </button>
          )}
          {hasEffects && (
            <button
              type="button"
              className={`flex-1 rounded-lg py-1.5 text-xs font-medium ${tab === 'effects' ? 'bg-raised text-text-high' : 'text-text-low hover:text-text-high'}`}
              onClick={() => setTab('effects')}
            >
              Effects
            </button>
          )}
        </div>
        )}
        <div className="min-h-0 flex-1">
          {tab === 'media' && hasMedia ? <MediaBinPanel /> : hasEffects ? <EffectsPanel /> : null}
        </div>
      </div>
      <ResizeHandle
        orientation="vertical"
        value={leftWidth}
        min={160}
        max={480}
        onChange={(v) => setPanelSize('leftWidth', v)}
      />
    </>
  );
}

function CenterStage() {
  const hasViewer = useEditorStore((s) => s.hasPanel('viewer'));
  const hasScopes = useEditorStore((s) => s.hasPanel('scopes'));
  const hasColor = useEditorStore((s) => s.hasPanel('color'));
  const hasKeys = useEditorStore((s) => s.hasPanel('keyframes'));
  const hasInspector = useEditorStore((s) => s.hasPanel('inspector'));
  const layoutId = useEditorStore((s) => s.layout.id);
  const rightWidth = useEditorStore((s) => s.panelSizes.rightWidth);
  const scopesHeight = useEditorStore((s) => s.panelSizes.scopesHeight);
  const setPanelSize = useEditorStore((s) => s.setPanelSize);

  if (layoutId === 'color-suite') {
    return <ColorSuiteLayout />;
  }

  if (layoutId === 'secondary-color') {
    return (
      <div className="flex min-h-0 min-w-0 flex-1">
        {hasViewer && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <ViewerPanel />
          </div>
        )}
        <ResizeHandle
          orientation="vertical"
          value={rightWidth}
          min={240}
          max={560}
          invert
          onChange={(v) => setPanelSize('rightWidth', v)}
        />
        <div className="flex min-h-0 shrink-0 flex-col" style={{ width: rightWidth }}>
          {hasScopes && (
            <>
              <div className="shrink-0 overflow-hidden" style={{ height: scopesHeight }}>
                <ScopesPanel />
              </div>
              <ResizeHandle
                orientation="horizontal"
                value={scopesHeight}
                min={72}
                max={280}
                onChange={(v) => setPanelSize('scopesHeight', v)}
              />
            </>
          )}
          {hasColor && (
            <div className="min-h-0 flex-1 overflow-hidden">
              <ColorPanel />
            </div>
          )}
          {hasKeys && (
            <div className="min-h-0 flex-1 overflow-hidden border-t border-hairline">
              <KeyframesPanel />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (layoutId === 'viewer-only') {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {hasViewer && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <ViewerPanel />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {hasViewer && (
        <div className="min-h-0 flex-1">
          <ViewerPanel />
        </div>
      )}
      {hasColor && !hasInspector && (
        <div className="flex h-80 shrink-0 flex-col border-t border-hairline">
          {hasScopes && (
            <>
              <div className="shrink-0 overflow-hidden" style={{ height: scopesHeight }}>
                <ScopesPanel />
              </div>
              <ResizeHandle
                orientation="horizontal"
                value={scopesHeight}
                min={72}
                max={240}
                onChange={(v) => setPanelSize('scopesHeight', v)}
              />
            </>
          )}
          <div className="min-h-0 flex-1">
            <ColorPanel />
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  useKeyboardShortcuts();

  const theme = useEditorStore((s) => s.theme);
  const setTheme = useEditorStore((s) => s.setTheme);
  const setLayout = useEditorStore((s) => s.setLayout);
  const hasPanel = useEditorStore((s) => s.hasPanel);
  const showWindowManager = useEditorStore((s) => s.showWindowManager);
  const layout = useEditorStore((s) => s.layout);
  const rightWidth = useEditorStore((s) => s.panelSizes.rightWidth);
  const timelineHeight = useEditorStore((s) => s.panelSizes.timelineHeight);
  const setPanelSize = useEditorStore((s) => s.setPanelSize);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    const block = (e: Event) => e.preventDefault();
    document.addEventListener('contextmenu', block);
    return () => document.removeEventListener('contextmenu', block);
  }, []);

  // Block browser Alt+wheel page zoom — timeline uses Alt+scroll for zoom instead
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (e.altKey) e.preventDefault();
    };
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', onWheel, true);
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem('cinegrid-layout');
    } catch {
      /* ignore */
    }
    const params = new URLSearchParams(window.location.search);
    const layoutId = params.get('layout');
    if (layoutId) {
      setLayout(layoutId);
      // One-shot: strip so refresh returns to the default editor layout
      params.delete('layout');
      const qs = params.toString();
      const next =
        window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', next);
    }
    const themeParam = params.get('theme');
    if (themeParam === 'light' || themeParam === 'dark') setTheme(themeParam);

    let cancelled = false;
    void (async () => {
      const snap = await loadProjectSnapshot();
      if (cancelled || !snap) return;
      const s = useEditorStore.getState();
      // Hydrate shared project before / alongside tab sync so dual windows match
      if (
        projectWeight(snap) > projectWeight(s.project) ||
        (snap.updatedAt ?? 0) >= (s.project.updatedAt ?? 0)
      ) {
        s.setProject(snap, { recordHistory: false });
      }
    })().finally(() => {
      if (!cancelled) tabSync.start();
    });

    return () => {
      cancelled = true;
      tabSync.stop();
    };
  }, [setLayout, setTheme]);

  const showLeft = hasPanel('media' as PanelId) || hasPanel('effects' as PanelId);
  const showInspector = hasPanel('inspector' as PanelId);
  const showTimeline = hasPanel('timeline' as PanelId);
  const showToolbar = hasPanel('toolbar' as PanelId) || layout.role === 'primary';

  return (
    <div className="flex h-full flex-col bg-base text-text-high">
      {showToolbar && <Toolbar />}
      <div className="flex min-h-0 flex-1">
        {showLeft && <LeftBin />}
        <CenterStage />
        {showInspector && (
          <>
            <ResizeHandle
              orientation="vertical"
              value={rightWidth}
              min={240}
              max={560}
              invert
              onChange={(v) => setPanelSize('rightWidth', v)}
            />
            <div className="shrink-0" style={{ width: rightWidth }}>
              <InspectorPanel />
            </div>
          </>
        )}
        {hasPanel('export' as PanelId) && !showInspector && (
          <>
            <ResizeHandle
              orientation="vertical"
              value={rightWidth}
              min={240}
              max={560}
              invert
              onChange={(v) => setPanelSize('rightWidth', v)}
            />
            <div className="shrink-0 border-l border-hairline" style={{ width: rightWidth }}>
              <ExportPanel />
            </div>
          </>
        )}
      </div>
      {showTimeline && (
        <>
          <ResizeHandle
            orientation="horizontal"
            value={timelineHeight}
            min={120}
            max={480}
            invert
            onChange={(v) => setPanelSize('timelineHeight', v)}
          />
          <div className="flex shrink-0 overflow-hidden" style={{ height: timelineHeight }}>
            <TimelineToolsRail />
            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <TimelinePanel />
            </div>
          </div>
        </>
      )}
      {(showWindowManager || hasPanel('windowManager' as PanelId)) && showWindowManager && (
        <WindowManagerPanel />
      )}
    </div>
  );
}
